import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import ProPlusGate from "@/components/ProPlusGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  FileText, FilePlus2, Receipt, Scroll, Send, Trash2, Download, Copy, Check,
  Loader2, Plus, ArrowRight, CircleDollarSign, PencilLine,
} from "lucide-react";
import { toast } from "sonner";

const money = (c, cur = "USD") =>
  `${cur === "USD" ? "$" : cur + " "}${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function VendorDocuments() {
  return (
    <ProPlusGate
      feature="Invoices & Contracts"
      pitch="Send branded invoices and contracts your clients can pay or sign online. Everything you need to look professional on a private booking, a custom order, or a side gig."
      perks={[
        "Branded PDF invoices with your logo + payment terms",
        "Email delivery to the client via Resend (PDF attached)",
        "Stripe-hosted \"Pay this invoice\" link — 3% platform fee only",
        "Contract templates: Market Booking, Private Booking, Custom",
        "Light e-signature: typed name + IP + timestamp",
        "Auto-record paid invoices in your Bookkeeping",
      ]}
    >
      <VendorDocumentsInner />
    </ProPlusGate>
  );
}

function VendorDocumentsInner() {
  const [tab, setTab] = useState("invoices");
  const [invoices, setInvoices] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [editingContract, setEditingContract] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const [inv, ctr] = await Promise.all([
        api.get("/documents/invoices"),
        api.get("/documents/contracts"),
      ]);
      setInvoices(inv.data.items || []);
      setContracts(ctr.data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10" data-testid="vendor-documents-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="overline" style={{ color: "var(--terracotta)" }}>
            Documents · Maker Pro+
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl mt-2">Invoices & Contracts</h1>
          <p className="mt-2 text-sm max-w-2xl" style={{ color: "var(--ink-soft)" }}>
            Create professional invoices and contracts, email them to clients, and let them pay or
            sign online.
          </p>
        </div>
        <Link
          to="/vendor/dashboard"
          className="text-sm underline"
          style={{ color: "var(--ink-soft)" }}
          data-testid="documents-back-link"
        >
          ← Back to dashboard
        </Link>
      </div>

      {/* Tabs */}
      <div className="mt-8 flex items-center gap-2 border-b" style={{ borderColor: "var(--sand)" }}>
        <button
          data-testid="documents-tab-invoices"
          onClick={() => setTab("invoices")}
          className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px ${tab === "invoices" ? "" : "opacity-60"}`}
          style={{ borderColor: tab === "invoices" ? "var(--terracotta)" : "transparent", color: tab === "invoices" ? "var(--terracotta)" : "inherit" }}
        >
          <Receipt className="inline h-4 w-4 mr-1.5" /> Invoices
          <span className="ml-1.5 opacity-70">({invoices.length})</span>
        </button>
        <button
          data-testid="documents-tab-contracts"
          onClick={() => setTab("contracts")}
          className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px ${tab === "contracts" ? "" : "opacity-60"}`}
          style={{ borderColor: tab === "contracts" ? "var(--terracotta)" : "transparent", color: tab === "contracts" ? "var(--terracotta)" : "inherit" }}
        >
          <Scroll className="inline h-4 w-4 mr-1.5" /> Contracts
          <span className="ml-1.5 opacity-70">({contracts.length})</span>
        </button>
      </div>

      {/* Action row */}
      <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm" style={{ color: "var(--ink-soft)" }}>
          {tab === "invoices" ? "Draft, send, and get paid." : "Draft contracts your clients can sign online."}
        </div>
        {tab === "invoices" ? (
          <Button
            data-testid="new-invoice-btn"
            onClick={() => setEditingInvoice({})}
            className="btn-primary rounded-full px-5 py-2.5 inline-flex items-center gap-2"
          >
            <FilePlus2 className="h-4 w-4" /> New invoice
          </Button>
        ) : (
          <Button
            data-testid="new-contract-btn"
            onClick={() => setEditingContract({})}
            className="btn-primary rounded-full px-5 py-2.5 inline-flex items-center gap-2"
          >
            <FilePlus2 className="h-4 w-4" /> New contract
          </Button>
        )}
      </div>

      {loading ? (
        <div className="mt-10 text-center text-sm" style={{ color: "var(--ink-soft)" }}>
          <Loader2 className="inline h-5 w-5 animate-spin mr-2" /> Loading…
        </div>
      ) : tab === "invoices" ? (
        <InvoiceList invoices={invoices} onEdit={(inv) => setEditingInvoice(inv)} onChange={reload} />
      ) : (
        <ContractList contracts={contracts} onEdit={(ctr) => setEditingContract(ctr)} onChange={reload} />
      )}

      {editingInvoice && (
        <InvoiceDialog
          initial={editingInvoice}
          onClose={() => setEditingInvoice(null)}
          onSaved={() => { setEditingInvoice(null); reload(); }}
        />
      )}
      {editingContract && (
        <ContractDialog
          initial={editingContract}
          onClose={() => setEditingContract(null)}
          onSaved={() => { setEditingContract(null); reload(); }}
        />
      )}
    </div>
  );
}

// =============================================================================
// INVOICE LIST + ACTIONS
// =============================================================================
function InvoiceList({ invoices, onEdit, onChange }) {
  if (!invoices.length) {
    return (
      <div
        data-testid="invoices-empty"
        className="mt-10 card-warm rounded-sm p-10 text-center"
        style={{ color: "var(--ink-soft)" }}
      >
        <Receipt className="h-10 w-10 mx-auto mb-3 opacity-50" />
        <p className="font-serif text-xl" style={{ color: "var(--ink)" }}>
          No invoices yet
        </p>
        <p className="mt-2 text-sm">Create your first invoice to get paid.</p>
      </div>
    );
  }
  return (
    <div className="mt-6 card-warm rounded-sm overflow-hidden" data-testid="invoices-list">
      <table className="w-full text-sm">
        <thead>
          <tr style={{ background: "var(--sand)" }}>
            <th className="text-left p-3 font-semibold">Number</th>
            <th className="text-left p-3 font-semibold">Client</th>
            <th className="text-left p-3 font-semibold">Issue</th>
            <th className="text-right p-3 font-semibold">Total</th>
            <th className="text-left p-3 font-semibold">Status</th>
            <th className="text-right p-3"></th>
          </tr>
        </thead>
        <tbody>
          {invoices.map((inv) => (
            <InvoiceRow key={inv.id} inv={inv} onEdit={onEdit} onChange={onChange} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InvoiceRow({ inv, onEdit, onChange }) {
  const [busy, setBusy] = useState(false);
  const [showSend, setShowSend] = useState(false);
  const statusColor = inv.status === "paid" ? "var(--moss)" : inv.status === "sent" ? "var(--gold)" : "var(--ink-soft)";

  const del = async () => {
    if (!window.confirm("Delete this invoice draft?")) return;
    setBusy(true);
    try {
      await api.delete(`/documents/invoices/${inv.id}`);
      toast.success("Invoice deleted.");
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const markPaid = async () => {
    setBusy(true);
    try {
      await api.post(`/documents/invoices/${inv.id}/mark-paid`, { record_as_income: true });
      toast.success("Marked as paid and recorded in Bookkeeping.");
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async () => {
    setBusy(true);
    try {
      const resp = await api.get(`/documents/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Invoice-${inv.number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <tr className="border-t" style={{ borderColor: "var(--sand)" }} data-testid={`invoice-row-${inv.id}`}>
        <td className="p-3 font-semibold">{inv.number}</td>
        <td className="p-3">
          <div>{inv.bill_to?.name}</div>
          {inv.bill_to?.email && <div className="text-xs" style={{ color: "var(--ink-soft)" }}>{inv.bill_to.email}</div>}
        </td>
        <td className="p-3 text-xs" style={{ color: "var(--ink-soft)" }}>{inv.issue_date || inv.created_at?.slice(0, 10)}</td>
        <td className="p-3 text-right font-semibold">{money(inv.total_cents, inv.currency)}</td>
        <td className="p-3">
          <span
            data-testid={`invoice-status-${inv.id}`}
            className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold uppercase"
            style={{ background: "rgba(107,47,186,0.08)", color: statusColor }}
          >
            {inv.status}
          </span>
        </td>
        <td className="p-3 text-right">
          <div className="inline-flex items-center gap-1.5">
            {inv.status === "draft" && (
              <Button
                data-testid={`invoice-edit-${inv.id}`}
                size="sm"
                variant="outline"
                onClick={() => onEdit(inv)}
                disabled={busy}
              >
                <PencilLine className="h-3.5 w-3.5" />
              </Button>
            )}
            {inv.status === "draft" && (
              <Button
                data-testid={`invoice-send-${inv.id}`}
                size="sm"
                className="btn-primary"
                onClick={() => setShowSend(true)}
                disabled={busy}
              >
                <Send className="h-3.5 w-3.5 mr-1" /> Send
              </Button>
            )}
            {inv.status === "sent" && (
              <Button
                data-testid={`invoice-markpaid-${inv.id}`}
                size="sm"
                variant="outline"
                onClick={markPaid}
                disabled={busy}
              >
                <Check className="h-3.5 w-3.5 mr-1" /> Mark paid
              </Button>
            )}
            <Button
              data-testid={`invoice-pdf-${inv.id}`}
              size="sm"
              variant="outline"
              onClick={downloadPdf}
              disabled={busy}
              title="Download PDF"
            >
              <Download className="h-3.5 w-3.5" />
            </Button>
            {inv.status === "draft" && (
              <Button
                data-testid={`invoice-delete-${inv.id}`}
                size="sm"
                variant="ghost"
                onClick={del}
                disabled={busy}
                title="Delete draft"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </td>
      </tr>
      {showSend && (
        <SendDocDialog
          kind="invoice"
          doc={inv}
          onClose={() => setShowSend(false)}
          onSent={() => { setShowSend(false); onChange(); }}
        />
      )}
    </>
  );
}

// =============================================================================
// CONTRACT LIST + ACTIONS
// =============================================================================
function ContractList({ contracts, onEdit, onChange }) {
  if (!contracts.length) {
    return (
      <div
        data-testid="contracts-empty"
        className="mt-10 card-warm rounded-sm p-10 text-center"
        style={{ color: "var(--ink-soft)" }}
      >
        <Scroll className="h-10 w-10 mx-auto mb-3 opacity-50" />
        <p className="font-serif text-xl" style={{ color: "var(--ink)" }}>
          No contracts yet
        </p>
        <p className="mt-2 text-sm">Start a contract from a template or write your own.</p>
      </div>
    );
  }
  return (
    <div className="mt-6 card-warm rounded-sm overflow-hidden" data-testid="contracts-list">
      <table className="w-full text-sm">
        <thead>
          <tr style={{ background: "var(--sand)" }}>
            <th className="text-left p-3 font-semibold">Number</th>
            <th className="text-left p-3 font-semibold">Title</th>
            <th className="text-left p-3 font-semibold">Client</th>
            <th className="text-left p-3 font-semibold">Template</th>
            <th className="text-left p-3 font-semibold">Status</th>
            <th className="text-right p-3"></th>
          </tr>
        </thead>
        <tbody>
          {contracts.map((ctr) => (
            <ContractRow key={ctr.id} ctr={ctr} onEdit={onEdit} onChange={onChange} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ContractRow({ ctr, onEdit, onChange }) {
  const [busy, setBusy] = useState(false);
  const [showSend, setShowSend] = useState(false);
  const statusColor = ctr.status === "signed" ? "var(--moss)" : ctr.status === "sent" ? "var(--gold)" : "var(--ink-soft)";
  const client = ctr.parties?.client || {};

  const del = async () => {
    if (!window.confirm("Delete this contract draft?")) return;
    setBusy(true);
    try {
      await api.delete(`/documents/contracts/${ctr.id}`);
      toast.success("Contract deleted.");
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async () => {
    setBusy(true);
    try {
      const resp = await api.get(`/documents/contracts/${ctr.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Contract-${ctr.number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <tr className="border-t" style={{ borderColor: "var(--sand)" }} data-testid={`contract-row-${ctr.id}`}>
        <td className="p-3 font-semibold">{ctr.number}</td>
        <td className="p-3">{ctr.title}</td>
        <td className="p-3">
          <div>{client.name}</div>
          {client.email && <div className="text-xs" style={{ color: "var(--ink-soft)" }}>{client.email}</div>}
        </td>
        <td className="p-3 text-xs" style={{ color: "var(--ink-soft)" }}>{ctr.template}</td>
        <td className="p-3">
          <span
            data-testid={`contract-status-${ctr.id}`}
            className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold uppercase"
            style={{ background: "rgba(107,47,186,0.08)", color: statusColor }}
          >
            {ctr.status}
          </span>
        </td>
        <td className="p-3 text-right">
          <div className="inline-flex items-center gap-1.5">
            {ctr.status === "draft" && (
              <Button size="sm" variant="outline" onClick={() => onEdit(ctr)} disabled={busy} data-testid={`contract-edit-${ctr.id}`}>
                <PencilLine className="h-3.5 w-3.5" />
              </Button>
            )}
            {ctr.status === "draft" && (
              <Button size="sm" className="btn-primary" onClick={() => setShowSend(true)} disabled={busy} data-testid={`contract-send-${ctr.id}`}>
                <Send className="h-3.5 w-3.5 mr-1" /> Send
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={downloadPdf} disabled={busy} title="Download PDF" data-testid={`contract-pdf-${ctr.id}`}>
              <Download className="h-3.5 w-3.5" />
            </Button>
            {ctr.status === "draft" && (
              <Button size="sm" variant="ghost" onClick={del} disabled={busy} title="Delete draft" data-testid={`contract-delete-${ctr.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </td>
      </tr>
      {showSend && (
        <SendDocDialog
          kind="contract"
          doc={ctr}
          onClose={() => setShowSend(false)}
          onSent={() => { setShowSend(false); onChange(); }}
        />
      )}
    </>
  );
}

// =============================================================================
// INVOICE DIALOG
// =============================================================================
function InvoiceDialog({ initial, onClose, onSaved }) {
  const isNew = !initial.id;
  const [billToName, setBillToName] = useState(initial.bill_to?.name || "");
  const [billToEmail, setBillToEmail] = useState(initial.bill_to?.email || "");
  const [billToAddress, setBillToAddress] = useState(initial.bill_to?.address || "");
  const [issueDate, setIssueDate] = useState(initial.issue_date || new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState(initial.due_date || "");
  const [currency, setCurrency] = useState(initial.currency || "USD");
  const [taxPct, setTaxPct] = useState(((initial.tax_rate_bps || 0) / 100).toString());
  const [discountCents, setDiscountCents] = useState(initial.discount_cents || 0);
  const [notes, setNotes] = useState(initial.notes || "");
  const [items, setItems] = useState(
    (initial.line_items && initial.line_items.length)
      ? initial.line_items.map((li) => ({ ...li }))
      : [{ description: "", qty: 1, unit_amount_cents: 0 }]
  );
  const [saving, setSaving] = useState(false);

  const subtotal = items.reduce((s, i) => s + (i.qty || 0) * (i.unit_amount_cents || 0), 0);
  const discountCapped = Math.max(0, Math.min(discountCents || 0, subtotal));
  const taxable = Math.max(0, subtotal - discountCapped);
  const taxBps = Math.max(0, Math.round(parseFloat(taxPct || "0") * 100));
  const tax = Math.floor(taxable * taxBps / 10000);
  const total = taxable + tax;

  const addItem = () => setItems([...items, { description: "", qty: 1, unit_amount_cents: 0 }]);
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));
  const updateItem = (idx, patch) => setItems(items.map((it, i) => i === idx ? { ...it, ...patch } : it));

  const save = async () => {
    if (!billToName.trim()) { toast.error("Client name is required."); return; }
    const validItems = items.filter((i) => (i.description || "").trim() && (i.qty || 0) > 0);
    if (!validItems.length) { toast.error("Add at least one line item."); return; }
    setSaving(true);
    const payload = {
      bill_to: { name: billToName, email: billToEmail || null, address: billToAddress || null },
      line_items: validItems.map((i) => ({
        description: i.description,
        qty: parseInt(i.qty) || 1,
        unit_amount_cents: Math.round(parseFloat(i.unit_amount_cents) || 0),
      })),
      currency,
      issue_date: issueDate || null,
      due_date: dueDate || null,
      tax_rate_bps: taxBps,
      discount_cents: parseInt(discountCents) || 0,
      notes: notes || null,
    };
    try {
      if (isNew) await api.post("/documents/invoices", payload);
      else await api.patch(`/documents/invoices/${initial.id}`, payload);
      toast.success(isNew ? "Draft invoice created." : "Invoice updated.");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="invoice-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "New invoice" : `Edit ${initial.number}`}</DialogTitle>
          <DialogDescription>Fill in your client, line items, and terms.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--ink-soft)" }}>Client name</label>
              <Input data-testid="invoice-bill-to-name" value={billToName} onChange={(e) => setBillToName(e.target.value)} placeholder="Jane Smith" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--ink-soft)" }}>Client email</label>
              <Input data-testid="invoice-bill-to-email" value={billToEmail} onChange={(e) => setBillToEmail(e.target.value)} placeholder="jane@example.com" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--ink-soft)" }}>Billing address (optional)</label>
            <Input data-testid="invoice-bill-to-address" value={billToAddress} onChange={(e) => setBillToAddress(e.target.value)} placeholder="123 Main St, City, State" />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--ink-soft)" }}>Issue date</label>
              <Input data-testid="invoice-issue-date" type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--ink-soft)" }}>Due date</label>
              <Input data-testid="invoice-due-date" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--ink-soft)" }}>Currency</label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger data-testid="invoice-currency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                  <SelectItem value="GBP">GBP</SelectItem>
                  <SelectItem value="CAD">CAD</SelectItem>
                  <SelectItem value="AUD">AUD</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--ink-soft)" }}>Tax %</label>
              <Input data-testid="invoice-tax-pct" type="number" step="0.01" min="0" max="50" value={taxPct} onChange={(e) => setTaxPct(e.target.value)} />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--ink-soft)" }}>Line items</label>
              <Button size="sm" variant="outline" onClick={addItem} data-testid="invoice-add-line">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add line
              </Button>
            </div>
            <div className="space-y-2">
              {items.map((item, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`invoice-line-${idx}`}>
                  <Input
                    data-testid={`invoice-line-desc-${idx}`}
                    className="col-span-6"
                    placeholder="Description"
                    value={item.description}
                    onChange={(e) => updateItem(idx, { description: e.target.value })}
                  />
                  <Input
                    data-testid={`invoice-line-qty-${idx}`}
                    className="col-span-2"
                    type="number" min="1" placeholder="Qty"
                    value={item.qty}
                    onChange={(e) => updateItem(idx, { qty: e.target.value })}
                  />
                  <Input
                    data-testid={`invoice-line-rate-${idx}`}
                    className="col-span-3"
                    type="number" step="0.01" placeholder="Rate (cents)"
                    value={item.unit_amount_cents}
                    onChange={(e) => updateItem(idx, { unit_amount_cents: e.target.value })}
                  />
                  <Button
                    data-testid={`invoice-line-remove-${idx}`}
                    size="sm" variant="ghost" className="col-span-1"
                    onClick={() => removeItem(idx)}
                    disabled={items.length === 1}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
            <p className="mt-2 text-xs" style={{ color: "var(--ink-soft)" }}>
              Rates are in cents — e.g. 2500 = $25.00. Use this to avoid rounding errors on your invoice.
            </p>
          </div>

          <div className="rounded-sm p-4" style={{ background: "var(--sand)" }}>
            <div className="flex justify-between text-sm">
              <span>Subtotal</span><span data-testid="invoice-subtotal">{money(subtotal, currency)}</span>
            </div>
            {discountCapped > 0 && (
              <div className="flex justify-between text-sm">
                <span>Discount</span><span>-{money(discountCapped, currency)}</span>
              </div>
            )}
            {tax > 0 && (
              <div className="flex justify-between text-sm">
                <span>Tax ({(taxBps / 100).toFixed(2)}%)</span><span>{money(tax, currency)}</span>
              </div>
            )}
            <div className="flex justify-between text-lg font-semibold mt-2 pt-2 border-t" style={{ borderColor: "var(--border, #e5e0d5)" }}>
              <span>Total</span><span data-testid="invoice-total">{money(total, currency)}</span>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--ink-soft)" }}>Notes (optional)</label>
            <Textarea
              data-testid="invoice-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Payment terms, thank-you message, etc."
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="invoice-cancel">Cancel</Button>
          <Button className="btn-primary" onClick={save} disabled={saving} data-testid="invoice-save">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {isNew ? "Save draft" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// CONTRACT DIALOG
// =============================================================================
function ContractDialog({ initial, onClose, onSaved }) {
  const isNew = !initial.id;
  const [title, setTitle] = useState(initial.title || "");
  const [template, setTemplate] = useState(initial.template || "market_booking");
  const [clientName, setClientName] = useState(initial.parties?.client?.name || "");
  const [clientEmail, setClientEmail] = useState(initial.parties?.client?.email || "");
  const [clientAddress, setClientAddress] = useState(initial.parties?.client?.address || "");
  const [intro, setIntro] = useState(initial.intro || "");
  const [sections, setSections] = useState(initial.sections || []);
  const [notes, setNotes] = useState(initial.notes || "");
  const [variables, setVariables] = useState(initial.variables || {});
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    api.get("/documents/contracts/templates").then((r) => setTemplates(r.data.templates || [])).catch(() => {});
  }, []);

  const updateSection = (idx, patch) => setSections(sections.map((s, i) => i === idx ? { ...s, ...patch } : s));
  const addSection = () => setSections([...sections, { heading: "", body: "" }]);
  const removeSection = (idx) => setSections(sections.filter((_, i) => i !== idx));

  const commonVars = ["vendor_name", "client_name", "event_title", "event_date", "event_location",
                       "deposit", "deposit_due", "balance", "balance_due", "total", "cancel_deadline",
                       "service_description", "load_in_time", "deadline", "revisions"];

  const save = async () => {
    if (!title.trim()) { toast.error("Contract title is required."); return; }
    if (!clientName.trim()) { toast.error("Client name is required."); return; }
    setSaving(true);
    const payload = {
      title,
      template,
      client_name: clientName,
      client_email: clientEmail || null,
      client_address: clientAddress || null,
      variables,
      intro: intro || null,
      sections: sections.filter((s) => s.heading || s.body),
      notes: notes || null,
    };
    try {
      if (isNew) await api.post("/documents/contracts", payload);
      else await api.patch(`/documents/contracts/${initial.id}`, payload);
      toast.success(isNew ? "Draft contract created." : "Contract updated.");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const showVars = template !== "blank" && sections.length === 0;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="contract-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "New contract" : `Edit ${initial.number}`}</DialogTitle>
          <DialogDescription>Pick a template or write your own clauses. Variables in {"{curly braces}"} are filled from the fields below.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--ink-soft)" }}>Contract title</label>
              <Input data-testid="contract-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Smith Wedding — Private Catering" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--ink-soft)" }}>Template</label>
              <Select value={template} onValueChange={setTemplate}>
                <SelectTrigger data-testid="contract-template"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--ink-soft)" }}>Client name</label>
              <Input data-testid="contract-client-name" value={clientName} onChange={(e) => setClientName(e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--ink-soft)" }}>Client email</label>
              <Input data-testid="contract-client-email" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} placeholder="required to send contract" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--ink-soft)" }}>Client address (optional)</label>
              <Input data-testid="contract-client-address" value={clientAddress} onChange={(e) => setClientAddress(e.target.value)} />
            </div>
          </div>

          {showVars && (
            <div className="rounded-sm p-4" style={{ background: "var(--sand)" }} data-testid="contract-variables">
              <div className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: "var(--ink-soft)" }}>Template variables</div>
              <p className="text-xs mb-3" style={{ color: "var(--ink-soft)" }}>
                These fill in the <code>{"{placeholders}"}</code> in your template. Leave blank and they'll show as <code>[name]</code> in the PDF so you remember to fill them in.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {commonVars.map((key) => (
                  <div key={key}>
                    <label className="text-[11px] font-semibold" style={{ color: "var(--ink-soft)" }}>{key}</label>
                    <Input
                      data-testid={`contract-var-${key}`}
                      value={variables[key] || ""}
                      onChange={(e) => setVariables({ ...variables, [key]: e.target.value })}
                      placeholder={key.replace(/_/g, " ")}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--ink-soft)" }}>
              Intro paragraph {template !== "blank" && "(auto-filled from template)"}
            </label>
            <Textarea
              data-testid="contract-intro"
              value={intro}
              onChange={(e) => setIntro(e.target.value)}
              rows={3}
              placeholder={template === "blank" ? "A short opener describing what this contract is for." : "Leave blank to use the template default."}
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--ink-soft)" }}>
                Sections (optional — overrides template)
              </label>
              <Button size="sm" variant="outline" onClick={addSection} data-testid="contract-add-section">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add section
              </Button>
            </div>
            {sections.length === 0 ? (
              <p className="text-xs" style={{ color: "var(--ink-soft)" }}>
                Leave empty to use the template's sections (with your variables filled in). Add your own to override.
              </p>
            ) : (
              <div className="space-y-3">
                {sections.map((s, idx) => (
                  <div key={idx} className="rounded-sm border p-3" style={{ borderColor: "var(--sand)" }} data-testid={`contract-section-${idx}`}>
                    <div className="flex items-center justify-between mb-2 gap-2">
                      <Input
                        data-testid={`contract-section-heading-${idx}`}
                        placeholder="Section heading"
                        value={s.heading || ""}
                        onChange={(e) => updateSection(idx, { heading: e.target.value })}
                      />
                      <Button size="sm" variant="ghost" onClick={() => removeSection(idx)} data-testid={`contract-section-remove-${idx}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <Textarea
                      data-testid={`contract-section-body-${idx}`}
                      placeholder="Section text — can reference {variables} from above"
                      value={s.body || ""}
                      onChange={(e) => updateSection(idx, { body: e.target.value })}
                      rows={3}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--ink-soft)" }}>Additional notes (optional)</label>
            <Textarea data-testid="contract-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="contract-cancel">Cancel</Button>
          <Button className="btn-primary" onClick={save} disabled={saving} data-testid="contract-save">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {isNew ? "Save draft" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// SEND DIALOG (shared for invoice + contract)
// =============================================================================
function SendDocDialog({ kind, doc, onClose, onSent }) {
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [sentUrl, setSentUrl] = useState("");

  const emailField = kind === "invoice" ? doc.bill_to?.email : doc.parties?.client?.email;
  const originUrl = window.location.origin;

  const send = async () => {
    setSending(true);
    try {
      const url = kind === "invoice"
        ? `/documents/invoices/${doc.id}/send`
        : `/documents/contracts/${doc.id}/send`;
      const resp = await api.post(url, { origin_url: originUrl, message: msg || null });
      setSentUrl(resp.data.public_url || "");
      toast.success(resp.data.email_status === "sent"
        ? `Sent to ${emailField}.`
        : `Shareable link ready — email delivery: ${resp.data.email_status}.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSending(false);
    }
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(sentUrl); toast.success("Link copied."); } catch {}
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid={`send-${kind}-dialog`}>
        <DialogHeader>
          <DialogTitle>Send {kind} to client</DialogTitle>
          <DialogDescription>
            {kind === "invoice"
              ? "Your client receives a PDF + a hosted page where they can pay online via Stripe."
              : "Your client receives a PDF + a hosted page where they can read and sign."}
          </DialogDescription>
        </DialogHeader>
        {!sentUrl ? (
          <div className="space-y-3">
            <div className="text-sm">
              <span style={{ color: "var(--ink-soft)" }}>Email:</span>{" "}
              <span className="font-semibold">{emailField || <em>(no email set — add one first)</em>}</span>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--ink-soft)" }}>Personal message (optional)</label>
              <Textarea
                data-testid={`send-${kind}-message`}
                value={msg}
                onChange={(e) => setMsg(e.target.value)}
                placeholder="A short note to your client."
                rows={3}
              />
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid={`send-${kind}-success`}>
            <p className="text-sm">Here's the public link your client can open:</p>
            <div className="rounded-sm border p-3 flex items-center gap-2" style={{ borderColor: "var(--sand)" }}>
              <code className="text-xs flex-1 overflow-x-auto">{sentUrl}</code>
              <Button size="sm" variant="outline" onClick={copyLink} data-testid={`send-${kind}-copy`}>
                <Copy className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
        <DialogFooter>
          {!sentUrl ? (
            <>
              <Button variant="outline" onClick={onClose} disabled={sending}>Cancel</Button>
              <Button className="btn-primary" onClick={send} disabled={sending || !emailField} data-testid={`send-${kind}-submit`}>
                {sending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                <Send className="h-4 w-4 mr-2" /> Send to {emailField || "client"}
              </Button>
            </>
          ) : (
            <Button className="btn-primary" onClick={onSent} data-testid={`send-${kind}-done`}>Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
